import { UserCommunicationPreference, UserGroup } from '@provision/entity-types';
import { gql, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { useNotifications } from 'src/utils/hooks';
import { UserGroupRules } from './group-rules';
import { PlatformUser } from './platform-user';

export type SignalNotificationOption = {
  value: string;
  label: string;
  id: string;
  requirePhone: boolean;
};

export enum CommunicationChannel {
  Email = 'email',
  SMS = 'sms',
  PhoneCall = 'phone-call',
}

const MANAGE_USER_NOTIFICATION_PREFERENCES_MUTATION = gql`
  mutation ManageUserCommunicationPreferences(
    $data: ManageUserCommunicationPreferencesDataInput!
  ) {
    manageUserCommunicationPreferences(params: $data) {
      userId
      userCommunicationPreferences {
        id
        communicationChannel
        priority
      }
    }
  }
`;

interface ManageCommunicationPreferences {
  userId: string;
  userCommunicationPreferences: Partial<UserCommunicationPreference>[];
  userGroups: UserGroup[];
}

export function useSignalNotificationsList(): SignalNotificationOption[] {
  return [
    {
      value: CommunicationChannel.Email,
      id: CommunicationChannel.Email,
      label: 'Email',
      requirePhone: false,
    },
    {
      value: CommunicationChannel.SMS,
      id: CommunicationChannel.SMS,
      label: 'SMS',
      requirePhone: true,
    },
    {
      value: CommunicationChannel.PhoneCall,
      id: CommunicationChannel.PhoneCall,
      label: 'Phone Call',
      requirePhone: true,
    },
  ];
}

export const useManageUserNotificationPreferences = () => {
  const { showErrorAlert } = useNotifications();

  const [manageCommunicationPreferencesMutation] = useMutation<{
    manageUserCommunicationPreferences: ManageCommunicationPreferences;
  }>(MANAGE_USER_NOTIFICATION_PREFERENCES_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message =
        'Something went wrong managing user communication preferences, please try again';
      showErrorAlert(message);
    },
  });

  const canManageNotificationPreferences = (user?: Partial<PlatformUser>): boolean => {
    if (!user?.userGroups?.length) {
      return false;
    }

    return user.userGroups.some(
      (item) =>
        [
          UserGroupRules.SuperAdmin,
          UserGroupRules.Admin,
          UserGroupRules.ProvisionSignal,
        ].includes(item.id as UserGroupRules) ||
        item.ruleValues.find(
          (rule) =>
            (rule.userGroupRuleId as UserGroupRules) === UserGroupRules.ProvisionSignal,
        ),
    );
  };

  const manageUserCommunicationPreferences = useCallback(
    async (params: ManageCommunicationPreferences) => {
      if (
        !params.userCommunicationPreferences ||
        !params.userId ||
        !canManageNotificationPreferences({ userGroups: params.userGroups })
      ) {
        return;
      }

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const preparedUserCommunicationPreferences = params.userCommunicationPreferences.map(
        (item) => ({
          userId: params.userId,
          id: item.id,
          priority: item.priority,
          communicationChannel: item.communicationChannel,
        }),
      );
      const optimisticResponseData: ManageCommunicationPreferences = {
        userId: params.userId,
        userGroups: params.userGroups,
        userCommunicationPreferences: preparedUserCommunicationPreferences.map(
          (item, index) => ({
            ...item,
            id: `${tempId}${index}`,
          }),
        ),
      };

      const { data } = await manageCommunicationPreferencesMutation({
        variables: {
          data: {
            userCommunicationPreferences: preparedUserCommunicationPreferences,
            userId: params.userId,
          },
        },
        optimisticResponse: {
          manageUserCommunicationPreferences: optimisticResponseData,
        },
        update: (cache, { data: response }) => {
          const userId = response?.manageUserCommunicationPreferences?.userId;
          if (!userId) return;

          const id = cache.identify({
            __typename: 'PlatformUser',
            id: userId,
          });

          cache.modify({
            id,
            fields: {
              userCommunicationPreferences(_, { toReference }) {
                const preparedItems = (
                  response.manageUserCommunicationPreferences.userCommunicationPreferences ??
                  []
                )
                  .toSorted((a, b) =>
                    !isNullish(b.priority) && !isNullish(a.priority)
                      ? b.priority - a.priority
                      : 0,
                  )
                  .map((item) =>
                    toReference(
                      {
                        ...item,
                        __typename: 'UserCommunicationPreference',
                      },
                      true,
                    ),
                  );

                return [...preparedItems];
              },
            },
          });
        },
      });

      return data?.manageUserCommunicationPreferences;
    },
    [manageCommunicationPreferencesMutation],
  );

  return {
    manageUserCommunicationPreferences,
    canManageNotificationPreferences,
  };
};
